"use client";

import type { ComponentProps, ReactNode } from "react";
import { useWithdrawal, WithdrawalRoot } from "./context";

type FieldProps = Omit<
	ComponentProps<"input">,
	"name" | "value" | "onChange"
> & {
	name: "email" | "orderNumber";
	label?: ReactNode;
};

const Field = ({ name, label, ...props }: FieldProps) => {
	const { email, orderNumber, setEmail, setOrderNumber } = useWithdrawal();
	const value = name === "email" ? email : orderNumber;
	const setValue = name === "email" ? setEmail : setOrderNumber;

	return (
		<label data-withdrawal-field={name}>
			{label != null && <span data-withdrawal-label>{label}</span>}
			<input
				type={name === "email" ? "email" : "text"}
				inputMode={name === "orderNumber" ? "numeric" : undefined}
				{...props}
				name={name}
				value={value}
				onChange={(event) => setValue(event.target.value)}
			/>
		</label>
	);
};

const Lookup = ({ children, ...rest }: ComponentProps<"div">) => {
	const { order, declared } = useWithdrawal();
	if (order || declared) return null;

	return (
		<div data-withdrawal-lookup {...rest}>
			{children}
		</div>
	);
};

const LookupButton = ({
	children,
	...props
}: Omit<ComponentProps<"button">, "onClick" | "type">) => {
	const { lookup, isLookingUp, email, orderNumber } = useWithdrawal();
	return (
		<button
			type="button"
			data-withdrawal-lookup-button
			data-loading={isLookingUp || undefined}
			{...props}
			disabled={props.disabled ?? (isLookingUp || !email || !orderNumber)}
			onClick={() => void lookup()}
		>
			{children}
		</button>
	);
};

const Items = (props: Omit<ComponentProps<"ul">, "children">) => {
	const { order, declared, getQuantity, setQuantity } = useWithdrawal();
	if (!order || declared) return null;

	return (
		<ul data-withdrawal-items {...props}>
			{order.items.map((line) => {
				const quantity = getQuantity(line.id);
				const remaining = line.quantity - (line.claimed ?? 0);
				const fullyClaimed = remaining <= 0;

				return (
					<li
						key={line.id}
						data-withdrawal-item
						data-claimed={fullyClaimed || undefined}
						data-selected={quantity > 0 || undefined}
					>
						<span data-withdrawal-item-name>
							{line.name}
							{line.size ? ` · ${line.size}` : ""}
						</span>
						{fullyClaimed ? (
							<span data-withdrawal-item-claimed>Already withdrawn</span>
						) : (
							<div data-withdrawal-quantity>
								<button
									type="button"
									onClick={() => setQuantity(line.id, quantity - 1)}
									disabled={quantity <= 0}
								>
									−
								</button>
								<span data-withdrawal-quantity-value>{quantity}</span>

								<button
									type="button"
									onClick={() => setQuantity(line.id, quantity + 1)}
									disabled={quantity >= remaining}
								>
									+
								</button>
								<span data-withdrawal-quantity-max>
									/ {remaining}
									{line.claimed ? ` (${line.claimed} withdrawn)` : ""}
								</span>
							</div>
						)}
					</li>
				);
			})}
		</ul>
	);
};

const ConfirmButton = ({ children, ...props }: ComponentProps<"button">) => {
	const { confirm, isSubmitting, selectedItems, order, declared } =
		useWithdrawal();
	if (!order || declared) return null;
	return (
		<button
			type="button"
			data-withdrawal-confirm-button
			data-loading={isSubmitting || undefined}
			{...props}
			disabled={props.disabled ?? (isSubmitting || selectedItems.length === 0)}
			onClick={() => void confirm()}
		>
			{children}
		</button>
	);
};

const Success = ({ children, ...rest }: ComponentProps<"div">) => {
	const { declared } = useWithdrawal();
	if (!declared) return null;

	return (
		<div data-withdrawal-success {...rest}>
			{children}
		</div>
	);
};

export const Withdrawal = {
	Root: WithdrawalRoot,
	Lookup,
	Field,
	LookupButton,
	Items,
	ConfirmButton,
	Success,
};
